import React, { ChangeEvent, FormEvent, useEffect, useReducer, useState, useRef } from 'react';
import partReducer, {
	BearingPartState,
	initialBearingPartState,
} from '../../../../components/bearing-search/reducers/reducer';
import TextInput from '../../../../components/bearing-search/components/forms/TextInput';
import PartSearch from '../../../../components/bearing-search/components/PartSearch';
import { text, unitList } from '../../../data';
import {
	BearingInfo,
	TimkenUnit,
} from '../../../../components/bearing-search/types/types';
import SelectDropdown from '../../../../components/bearing-search/components/forms/SelectDropdown';
import RelubricateConditions from '../relubricate-conditions/RelubricateConditions';
import greaseRelubricateByPartReducer, {
	initialRelubricateByPartNumberState,
} from './reducer';
import { bearingSearchUrl } from '../../../../components/bearing-search/api/api';
import { GreaseFrequencyConditionsPayload } from '../relubricate-conditions/reducer';
import { GreaseFrequencyPartPayload } from '../GreaseRelubrication';
import {
	updateToolParams,
	getPartNumberFromUrl,
	getPartStateFromParams
} from '../../../utils/engineering-tools';
import PartPreSearch from '../../../components/PartPreSearch';

interface RelubricateByPartNumberProps {
	typeCode: string;
	onSubmit: (
		payload: GreaseFrequencyPartPayload,
		bearing?: BearingPartState
	) => void;
	onClear: () => void;
}

const RelubricateByPartNumber: React.FC<RelubricateByPartNumberProps> = ( {
	typeCode,
	onSubmit,
	onClear,
} ) => {
	const handleFormChangeRef = useRef<any>(null);
	const [ isPartSelected, setIsPartSelected ] = useState<boolean>(null);
	const [ isValidated, setIsValidated ] = useState( false );
	const [ isPartOpen, setIsPartOpen ] = useState( false );
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ relubricationData, setRelubricationData ] = useState<
		GreaseFrequencyConditionsPayload | undefined
	>();
	const [ partState, partDispatch ] = useReducer(
		partReducer,
		initialBearingPartState,
	);

	const [ formState, dispatch ] = useReducer(
		greaseRelubricateByPartReducer,
		initialRelubricateByPartNumberState,
	);

	useEffect( () => {
		setIsValidated( false );
	}, [ typeCode ] );

	// Pass down part number to next recommended tool
	useEffect( () => {
		updateToolParams<BearingPartState>( partState );

		return () => {
			updateToolParams();
		};
	}, [ partState.marketPlaceDescription ] );

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		const value = event.target.value;
		setSearchInput( value );
		setIsPartSelected( false );
	};

	const searchPart = ( event: FormEvent ) => {
		event.preventDefault();

		if ( ! partState.partNumber ) {
			setIsPartOpen( true );
		}
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			partDispatch( {
				type: 'select_part',
				payload: part,
			} );
			setIsPartSelected( true );
			setSearchInput( part.marketPlaceDescription || '' );
		}

		setIsPartOpen( false );
	};

	const clearSearch = () => {
		setSearchInput( '' );
		setIsValidated( false );
		setIsPartSelected( false );

		partDispatch( {
			type: 'reset',
		} );

		dispatch( {
			type: 'reset',
		} );

		onClear();
	};

	const handleUnitChange = ( event: ChangeEvent<HTMLSelectElement> ) => {
		setIsValidated( false );

		dispatch( {
			type: 'change_unit',
			payload: event.target.value as TimkenUnit,
		} );

		handleFormChangeRef.current?.handleFormChange(event.target.value);
	};

	const handleConditionFormChange = (
		payload?: GreaseFrequencyConditionsPayload,
	) => {
		setIsValidated( false );
		setRelubricationData( payload );
	};

	const handleCalculate = ( event: FormEvent ) => {
		event.preventDefault();

		setIsValidated( true );

		if ( partState.partNumber && relubricationData ) {
			const { unit } = formState;

			const { bearingType, bearingSubType, partNumber, cupPartNumber } =
				partState;

			onSubmit(
				{
					unit,
					bearingType,
					bearingSubType,
					partNumber,
					cupPartNumber,
					...relubricationData,
				},
				partState,
			);
		}
	};

	useEffect(() => {
		const queryMap = new URLSearchParams( document.location.search );
		const savedPart = getPartStateFromParams( queryMap );

		if (queryMap.size == 1) {
			const partNumberFromUrl = getPartNumberFromUrl();
			if (partNumberFromUrl) {
				setSearchInput(partNumberFromUrl);
				partDispatch({
					type: 'select_part',
					payload: { marketPlaceDescription: partNumberFromUrl },
				});
				setIsPartSelected( true );
			}
		} else if (queryMap.size > 1) {
			if ( savedPart.partNumber ) {
				partDispatch( {
					type: 'reset',
					payload: savedPart,
				} );
				setIsPartSelected( true );
				setSearchInput( savedPart.marketPlaceDescription || '' );
			}
		}
	}, []);

	const preSearchClickHandler = (e: React.MouseEvent<HTMLElement>) => {
		setIsPartSelected(true);
		searchPart(e);
	};

	return (
		<>
			<p className="t-engineering-tools__description">
				{text.dontKnowPartNumber}{' '}
				<a href={bearingSearchUrl}>{text.bearingSearch}</a>
			</p>

			<form onSubmit={searchPart}>
				<div className="t-engineering-tools__form-fields t-engineering-tools__form-fields--multi-column timken-tool-presearch-container">
					<TextInput
						id="grease_relubricate_part_number"
						required={true}
						label={text.partNumber}
						verified={!! partState.partNumber}
						name="partNumber"
						value={searchInput}
						disabled={!! partState.partNumber}
						placeholder={text.partNumber}
						message={
							partState.partNumber
								? `${ text.bearingType }: ${ partState.bearingTypeLabel }`
								: text.partWildCardHint
						}
						onChange={inputPartNumber}
					/>
					<PartPreSearch
						input={searchInput}
						isHidden={searchInput.length < 2 || isPartOpen || isPartSelected}
						unit={formState.unit}
						onClick={(e) => preSearchClickHandler(e)}
						verifyPartNumber={(value: string) => setSearchInput(value)}
					/>

					<SelectDropdown
						id="grease_relubricate_unit"
						name="unit"
						required={true}
						label={text.displayUnits}
						value={formState.unit}
						options={unitList}
						onChange={handleUnitChange}
					/>

					{! partState.partNumber && (
						<button
							className="btn btn--primary"
							type="submit"
							disabled={! searchInput}
						>
							{text.lookup}
						</button>
					)}
				</div>
			</form>

			{!! partState.partNumber && (
				<>
					<RelubricateConditions
						unit={formState.unit}
						isValidated={isValidated}
						onChange={handleConditionFormChange}
						onSubmit={handleCalculate}
						ref={handleFormChangeRef}
					/>

					<form onSubmit={handleCalculate}>
						<div>
							<div className="t-engineering-tools__terms-of-use"
								  dangerouslySetInnerHTML={{ __html: text.calculateTermsOfUse }}
							/>
							<div className="t-engineering-tools__search-actions">
								<button
									className="btn btn--primary"
									type="submit"
									disabled={isValidated}
								>
									{text.calculate}
								</button>

								<button
									className="btn btn--secondary"
									type="button"
									onClick={() => clearSearch()}
								>
									{text.clear}
								</button>
							</div>
						</div>
					</form>
				</>
			)}

			{isPartOpen && !! searchInput && (
				<PartSearch
					units={formState.unit}
					partNumber={searchInput}
					onSelect={verifyPart}
					onClose={() => setIsPartOpen( false )}
				/>
			)}
		</>
	);
};

export default RelubricateByPartNumber;
